import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  FormGroup,
  Alert,
  Grid
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GetUser, UpdateMate } from '../../services/profile';
import { GetCountries, GetCities, GetDistrict } from '../../services/location';

const EditMate = () => {
  const [formData, setFormData] = useState({
    country: 0,
    city: 0,
    district: 0,
    biography: '',
    roommate: false,
    hideProfile: false,
    preferredGender: 70 // Default: None (Herkes)
  });

  const [locations, setLocations] = useState({
    countries: [],
    cities: [],
    districts: []
  });

  const [isLoading, setIsLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    fetchUserData();
    fetchCountries();
  }, []);

  const fetchUserData = async () => {
    try {
      const response = await GetUser();
      if (response.statusCode === 200) {
        setFormData({
          country: response.data.country || 0,
          city: response.data.city || 0,
          district: response.data.district || 0,
          biography: response.data.biography || '',
          roommate: response.data.roommate || false,
          hideProfile: response.data.hideProfile || false,
          preferredGender: response.data.preferredGender || 70
        });
        
        if (response.data.country) {
          fetchCities(response.data.country);
        }
        if (response.data.city) {
          fetchDistricts(response.data.city);
        }
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(t('editMate.error'));
    } finally {
      setInitialLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await GetCountries();
      if (response.statusCode === 200) {
        setLocations(prev => ({
          ...prev,
          countries: response.data
        }));
      }
    } catch (error) {
      toast.error(t('editMate.error'));
    }
  };

  const fetchCities = async (countryId) => {
    try {
      const response = await GetCities(countryId);
      if (response.statusCode === 200) {
        setLocations(prev => ({
          ...prev,
          cities: response.data
        }));
      }
    } catch (error) {
      toast.error(t('editMate.error'));
    }
  };

  const fetchDistricts = async (cityId) => {
    try {
      const response = await GetDistrict(cityId);
      if (response.statusCode === 200) {
        setLocations(prev => ({
          ...prev,
          districts: response.data
        }));
      }
    } catch (error) {
      toast.error(t('editMate.error'));
    }
  };

  const handleCountryChange = (event) => {
    const countryId = event.target.value;
    setFormData(prev => ({
      ...prev,
      country: countryId,
      city: 0,
      district: 0
    }));
    fetchCities(countryId);
  };

  const handleCityChange = (event) => {
    const cityId = event.target.value;
    setFormData(prev => ({
      ...prev,
      city: cityId,
      district: 0
    }));
    fetchDistricts(cityId);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(false);

    if (!formData.country) {
      toast.error(t('editMate.countryRequired'));
      setIsLoading(false);
      return;
    }
    if (!formData.city) {
      toast.error(t('editMate.cityRequired'));
      setIsLoading(false);
      return;
    }
    if (!formData.district) {
      toast.error(t('editMate.districtRequired'));
      setIsLoading(false);
      return;
    }
    if (!formData.biography.trim()) {
      toast.error(t('editMate.biographyRequired'));
      setIsLoading(false);
      return;
    }
    formData.gender = formData.preferredGender;
    try {
      const response = await UpdateMate(formData);
      if (response.statusCode === 200) {
        setSuccess(true);
        toast.success(t('editMate.success'));
        navigate(-1);
      } else {
        setError(response.message || t('editMate.error'));
        toast.error(response.message || t('editMate.error'));
      }
    } catch (err) {
      console.error('Profil güncellenirken hata oluştu:', err);
      setError(t('editMate.error'));
      toast.error(t('editMate.error'));
    } finally {
      setIsLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        py: 4,
        px: 2,
        background: theme.palette.background.default
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={theme.palette.mode === 'dark' ? 3 : 1}
          sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}
        >
          <Typography variant="h5" component="h1" align="center" gutterBottom>
            {t('editMate.title')}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {t('editMate.success')}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>{t('editMate.country')}</InputLabel>
                  <Select
                    value={formData.country}
                    label={t('editMate.country')}
                    onChange={handleCountryChange}
                  >
                    {locations.countries.map(country => (
                      <MenuItem key={country.id} value={country.id}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>{t('editMate.city')}</InputLabel>
                  <Select
                    value={formData.city}
                    label={t('editMate.city')}
                    onChange={handleCityChange}
                    disabled={!formData.country}
                  >
                    {locations.cities.map(city => (
                      <MenuItem key={city.id} value={city.id}>
                        {city.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>{t('editMate.district')}</InputLabel>
                  <Select
                    value={formData.district}
                    label={t('editMate.district')}
                    onChange={(e) => setFormData(prev => ({ ...prev, district: e.target.value }))}
                    disabled={!formData.city}
                  >
                    {locations.districts.map(district => (
                      <MenuItem key={district.id} value={district.id}>
                        {district.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  margin="normal"
                  fullWidth
                  multiline
                  rows={4}
                  label={t('editMate.biography')}
                  value={formData.biography}
                  onChange={handleChange}
                  name="biography"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="preferred-gender-label">
                    {t('editMate.preferredGender')}
                  </InputLabel>
                  <Select
                    labelId="preferred-gender-label"
                    id="preferredGender"
                    name="preferredGender"
                    value={formData.preferredGender}
                    onChange={handleChange}
                    label={t('editMate.preferredGender')}
                  >
                    <MenuItem value={70}>{t('editMate.genderOptions.everyone')}</MenuItem>
                    <MenuItem value={104}>{t('editMate.genderOptions.onlyFemale')}</MenuItem>
                    <MenuItem value={201}>{t('editMate.genderOptions.onlyMale')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.roommate}
                        onChange={handleChange}
                        name="roommate"
                        color="primary"
                      />
                    }
                    label={t('editMate.visible')}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.hideProfile}
                        onChange={handleChange}
                        name="hideProfile"
                        color="primary"
                      />
                    }
                    label={t('editMate.maskProfile')}
                  />
                </FormGroup>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => navigate(-1)}
                    disabled={isLoading}
                  >
                    {t('common.back')}
                  </Button>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      t('common.submit')
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default EditMate; 